"use client";

import type { DeepPartial } from "@apollo/client/utilities";
import Image from "next/image";
import { use, useCallback, useEffect, useRef, useState } from "react";

import { EmptyState } from "@/components/shared/empty-state";
import { UserContext } from "@/contexts/user-context";
import type { UserAssociation } from "@/graphql/codegen/graphql";
import {
  useFeedSettings,
  useGetFeedList,
} from "@/hooks-api/use-feed-providers";
import { usePaginationRequest } from "@/hooks-api/use-pagination-request";
import { APP_NAME_GENERIC, DESO_APP_NAME } from "@/utils/constants";

import { FeedSettingsHeader } from "../feed-settings/feed-header";
import { FeedStoreItem } from "./feed-store-item";

const FEED_PROVIDERS_LIMIT = 20;

export const FeedStoreLayout = () => {
  const { getFeedListRequest } = useGetFeedList();
  const [feedList, setFeedList] = useState<DeepPartial<UserAssociation>[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const { getFeedSettingsRequest } = useFeedSettings();
  const [activeFeedId, setActiveFeedId] = useState<string | null>(null);
  const { currentUserPublicKey } = use(UserContext);
  const requestVersionRef = useRef(0);

  const hasMoreCreators = totalCount > loadedCount;

  const getFeedProviders = useCallback(async () => {
    const requestVersion = requestVersionRef.current;
    const extraVariables = {
      limit: FEED_PROVIDERS_LIMIT,
      offset: loadedCount,
    };

    try {
      const data = await getFeedListRequest({ extraVariables });
      if (requestVersion !== requestVersionRef.current) {
        return;
      }
      const totalCount = data?.userAssociations?.totalCount ?? 0;

      const newFeedList = data?.userAssociations?.nodes.filter(Boolean) ?? [];
      setTotalCount(totalCount);
      setLoadedCount((count) => count + newFeedList.length);
      setFeedList((feedList) => [...feedList, ...newFeedList]);
    } catch (error) {
      console.error("Failed to get notifications:", error);
    }
  }, [getFeedListRequest, loadedCount]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const data = await getFeedSettingsRequest();

      if (cancelled) {
        return;
      }

      const feedId = data?.userAssociations?.nodes[0]?.extraData.FeedId;
      setActiveFeedId(feedId);
    })();

    return () => {
      cancelled = true;
    };
  }, [getFeedSettingsRequest]);

  const { loadingMarkup, setInitialLoading, initialLoading } =
    usePaginationRequest({
      initialRequest: getFeedProviders,
      fetchRequest: getFeedProviders,
      hasMoreItems: hasMoreCreators,
    });

  useEffect(() => {
    if (currentUserPublicKey && !initialLoading) {
      requestVersionRef.current += 1;
      setFeedList([]);
      setLoadedCount(0);
      setTotalCount(0);
      setInitialLoading(true);
    }
  }, [currentUserPublicKey]);

  return (
    <div className="relative">
      <FeedSettingsHeader isActive="store" />
      <div className="flex flex-col">
        <div
          className="
            h-[100px] overflow-hidden
            sm:h-[140px]
          "
          style={{
            WebkitMaskImage:
              "linear-gradient(180deg, #000000FF 40%, #00000000 100%)",
          }}
        >
          <Image
            src="/assets/empty-9.png"
            className="w-full"
            width={1024}
            height={0}
            alt="Feed Banner"
          />
        </div>

        <div
          className="
            border-b border-border-light p-6 pt-0
            sm:p-6
          "
        >
          <h2
            className="
              mb-2 font-sans text-2xl font-semibold text-muted-foreground
            "
          >
            Browse feeds
          </h2>
          <p className="text-sm/5 text-muted">
            On {APP_NAME_GENERIC}, users are no longer locked into a single
            algorithm. Every user has control over their feed and can customize
            any parameter however they'd like to determine the output of content
            on their feed.{" "}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-6 p-6">
        {!initialLoading &&
          (feedList.length > 0 ? (
            feedList.map((feedItem, index) => (
              <FeedStoreItem
                key={index}
                feedItem={feedItem}
                setActiveFeedId={setActiveFeedId}
                activeFeedId={activeFeedId}
              />
            ))
          ) : (
            <EmptyState
              image="/assets/empty-14.png"
              title="Coming soon"
              description={`All content posted on Focus is stored on the completely open ${DESO_APP_NAME} Blockchain. This means anyone can build their own feed from the firehose, and charge people a subscription to use it, creating the first decentralized content curation marketplace.`}
            />
          ))}
      </div>
      {loadingMarkup}
    </div>
  );
};
